import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { MoreVertical } from 'lucide-react';
import Card from '../components/UI/Card';
import Badge from '../components/UI/Badge';
import Button from '../components/UI/Button';
import TradingChart from '../components/TradingFloor/TradingChart';
import OrderBook from '../components/TradingFloor/OrderBook';
import MarketDepth from '../components/TradingFloor/MarketDepth';
import StrategyDetails from '../components/TradingFloor/StrategyDetails';
import PositionPanel from '../components/TradingFloor/PositionPanel';
import DeleteStrategyModal from '../components/Strategies/DeleteStrategyModal';
import StopStrategyModal from '../components/Strategies/StopStrategyModal';
import { PageTransition, Spinner } from '@/components/motion';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '../components/UI/dropdown-menu';
import useContainerMobile from '../hooks/useContainerMobile';
import useWebSocket from '../hooks/useWebSocket';
import formatMoney from '@/lib/formatMoney';
import { normalizeSymbol } from '@/lib/utils';
import { strategiesAPI, tradesAPI, positionsAPI, plannedOrdersAPI } from '../services/api';

/* ---------- helpers reused from StrategyDetails ---------- */
const getStatusVariant = (status) => {
  const map = { running: 'success', stopped: 'neutral', paused: 'warning', error: 'danger', archived: 'neutral' };
  return map[status] || 'neutral';
};

const Sep = () => <span className="text-border/50">|</span>;

const Stat = ({ label, value, valueClass, small }) => (
  <span className={small ? 'text-xs' : ''}>
    <span className="text-muted-foreground/60">{label}</span>{' '}
    <span className={`font-semibold ${valueClass || 'text-foreground'}`}>{value}</span>
  </span>
);

/* ---------- Mobile strategy header card ---------- */
function MobileStrategyHeader({
  strategy,
  positions,
  closedToday = [],
  strategies,
  selectedStrategyId,
  onStrategyChange,
  onStrategyUpdate,
  livePrice,
}) {
  const navigate = useNavigate();
  const [actionLoading, setActionLoading] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [stopModalOpen, setStopModalOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpen(false);
    };
    if (dropdownOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dropdownOpen]);

  const handleAction = async (actionFn, actionName) => {
    setActionLoading(actionName);
    try {
      await actionFn(strategy.id);
      toast.success(`Strategy ${actionName} successful`);
      onStrategyUpdate?.();
    } catch (err) {
      toast.error(`Failed to ${actionName}: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleArchiveConfirm = async ({ liquidate } = {}) => {
    try {
      await strategiesAPI.archive(strategy.id, { liquidate });
      setArchiveModalOpen(false);
      toast.success('Strategy archived');
      onStrategyUpdate?.();
    } catch (err) {
      toast.error(`Failed to archive: ${err.message}`);
    }
  };

  const handleStopConfirm = () => {
    setStopModalOpen(false);
    onStrategyUpdate?.();
  };

  const totalPositionValue = positions.reduce((sum, pos) => sum + parseFloat(pos.quantity) * parseFloat(pos.current_price || pos.entry_price), 0);
  const totalUnrealized = positions.reduce((sum, pos) => sum + parseFloat(pos.unrealized_pnl || 0), 0);
  const totalRealized = positions.reduce((sum, pos) => sum + parseFloat(pos.realized_pnl || 0), 0)
    + closedToday.reduce((sum, pos) => sum + parseFloat(pos.realized_pnl || 0), 0);
  const totalPnL = totalUnrealized + totalRealized;

  const params = strategy.parameters || {};
  const currentPrice = livePrice != null ? livePrice
    : positions.length > 0
      ? parseFloat(positions[0].current_price || positions[0].entry_price)
      : null;
  const isRunning = strategy.status === 'running';
  const isStopped = strategy.status === 'stopped';
  const isPaused = strategy.status === 'paused';
  const isError = strategy.status === 'error';
  const isArchived = strategy.status === 'archived';
  const canSwitch = strategies.length > 1 && onStrategyChange;

  const paramEntries = [
    params.gridMode && ['Mode', params.gridMode],
    params.gridLevels && ['Levels', params.gridLevels],
    params.lowerBound != null && ['Lower', formatMoney(params.lowerBound)],
    params.upperBound != null && ['Upper', formatMoney(params.upperBound)],
    params.orderSize && ['Size', `${params.orderSize} shr`],
    params.rebalanceInterval && ['Rebal', `${params.rebalanceInterval}s`],
    params.lookbackPeriod && ['Lookback', `${params.lookbackPeriod}p`],
    params.entryThreshold != null && ['Entry', params.entryThreshold],
    params.exitThreshold != null && ['Exit', params.exitThreshold],
    params.stopLoss != null && ['SL', `${(params.stopLoss * 100).toFixed(1)}%`],
    params.takeProfit != null && ['TP', `${(params.takeProfit * 100).toFixed(1)}%`],
    params.maxPositions != null && ['Max Pos', params.maxPositions],
  ].filter(Boolean);

  const switcherMenu = dropdownOpen && (
    <div className="absolute top-full left-0 mt-2 w-[calc(100vw-2rem)] bg-card border border-border rounded-xl shadow-2xl z-50 overflow-hidden">
      <div className="px-4 py-3 border-b border-border">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Switch Strategy</p>
      </div>
      <div className="max-h-64 overflow-y-auto py-1">
        {strategies.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => { onStrategyChange(s.id); setDropdownOpen(false); }}
            className={`w-full text-left px-4 py-3 flex items-center justify-between gap-3 transition-colors ${
              s.id === strategy.id ? 'bg-primary/10' : 'hover:bg-white/[0.04]'
            }`}
          >
            <div className="min-w-0">
              <p className={`text-sm font-semibold truncate ${s.id === strategy.id ? 'text-primary' : 'text-foreground'}`}>{s.name}</p>
              <p className="text-xs text-muted-foreground capitalize">{s.type?.replace('_', ' ')} {s.parameters?.symbol ? `· ${s.parameters.symbol}` : ''}</p>
            </div>
            <Badge variant={getStatusVariant(s.status)}>{s.status}</Badge>
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <>
      <div className="bg-card border border-border rounded-lg px-3 py-2.5 relative">
        {/* Row 1: Strategy name + ticker */}
        <div className="flex items-center justify-between gap-2">
          <div className="relative min-w-0 flex-1" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => canSwitch && setDropdownOpen(!dropdownOpen)}
              className={`flex items-center gap-1.5 text-base font-bold text-foreground truncate ${canSwitch ? 'cursor-pointer hover:text-primary transition-colors' : 'cursor-default'}`}
            >
              <span className="truncate">{strategy.name}</span>
              {canSwitch && (
                <svg className={`w-3.5 h-3.5 flex-shrink-0 text-muted-foreground transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              )}
            </button>
            {switcherMenu}
          </div>
          <div className="flex items-baseline gap-2 flex-shrink-0">
            <span className="text-lg font-black tracking-tight text-primary">{params.symbol || 'N/A'}</span>
            {currentPrice != null && (
              <span className="text-base font-semibold text-foreground">{formatMoney(currentPrice)}</span>
            )}
          </div>

          {/* Ellipsis actions */}
          {!isArchived && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="flex-shrink-0 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent/20 transition-colors">
                  <MoreVertical className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[160px]">
                {(isStopped || isPaused || isError) && (
                  <DropdownMenuItem
                    disabled={!!actionLoading}
                    onClick={() => handleAction(isPaused ? strategiesAPI.resume : strategiesAPI.start, isPaused ? 'resumed' : 'started')}
                    className="text-green-400 focus:text-green-300"
                  >
                    {isPaused ? 'Resume' : 'Start'}
                  </DropdownMenuItem>
                )}
                {isRunning && (
                  <DropdownMenuItem
                    disabled={!!actionLoading}
                    onClick={() => handleAction(strategiesAPI.pause, 'paused')}
                    className="text-yellow-400 focus:text-yellow-300"
                  >
                    Pause
                  </DropdownMenuItem>
                )}
                {(isRunning || isPaused || isError) && (
                  <DropdownMenuItem
                    disabled={!!actionLoading}
                    onClick={() => setStopModalOpen(true)}
                    className="text-red-400 focus:text-red-300"
                  >
                    Stop
                  </DropdownMenuItem>
                )}
                {(isStopped || isError) && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate(`/strategies/builder/${strategy.id}`)}>
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem disabled={!!actionLoading} onClick={() => setArchiveModalOpen(true)}>
                      Archive
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Row 2: Status + mode + asset class badges */}
        <div className="flex items-center gap-1.5 mt-1.5">
          <Badge variant={getStatusVariant(strategy.status)}>{strategy.status}</Badge>
          <Badge variant={strategy.trading_mode === 'paper' ? 'info' : 'warning'}>
            {strategy.trading_mode === 'paper' ? 'PAPER' : 'LIVE'}
          </Badge>
          {strategy.asset_class && <Badge variant="neutral">{strategy.asset_class}</Badge>}
        </div>

        {/* Row 3: Financial stats */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-2 text-xs text-muted-foreground">
          <Stat label="Value" value={`$${totalPositionValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
          <Sep />
          <Stat label="Unreal." value={formatMoney(totalUnrealized, { sign: true })} valueClass={totalUnrealized >= 0 ? 'text-green-400' : 'text-red-400'} />
          <Sep />
          <Stat label="Real." value={formatMoney(totalRealized, { sign: true })} valueClass={totalRealized >= 0 ? 'text-green-400' : 'text-red-400'} />
          <Sep />
          <Stat label="P&L" value={formatMoney(totalPnL, { sign: true })} valueClass={totalPnL >= 0 ? 'text-green-400' : 'text-red-400'} />
          <Sep />
          <Stat label="Pos" value={positions.length} />
        </div>

        {/* Row 4: Strategy params */}
        {paramEntries.length > 0 && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1.5 pt-1.5 border-t border-white/5 text-xs">
            <span className="text-muted-foreground/60 uppercase tracking-wider font-medium">{strategy.type?.replace('_', ' ')}</span>
            {paramEntries.map(([label, value]) => (
              <Stat key={label} label={label} value={value} small />
            ))}
          </div>
        )}
      </div>

      <StopStrategyModal
        strategy={stopModalOpen ? strategy : null}
        open={stopModalOpen}
        onClose={() => setStopModalOpen(false)}
        onConfirm={handleStopConfirm}
      />

      <DeleteStrategyModal
        strategy={archiveModalOpen ? strategy : null}
        open={archiveModalOpen}
        onClose={() => setArchiveModalOpen(false)}
        onConfirm={handleArchiveConfirm}
        mode="archive"
      />
    </>
  );
}

/* ---------- Trade History list (shared between mobile/desktop) ---------- */
function TradeHistoryList({ trades }) {
  return (
    <div className="flex-1 overflow-y-auto space-y-2">
      {trades.length > 0 ? (
        trades.map((trade) => (
          <div
            key={trade.id}
            className={`p-3 rounded-lg border-l-4 ${
              trade.side === 'buy' ? 'bg-green-900/20 border-green-500' : 'bg-red-900/20 border-red-500'
            }`}
          >
            <div className="flex justify-between items-start mb-1">
              <span className="font-bold text-foreground">
                {trade.side.toUpperCase()} {parseFloat(trade.quantity).toFixed(2)} {trade.symbol}
              </span>
              <span className="text-sm text-muted-foreground">
                {formatMoney(trade.price)}
              </span>
            </div>
            <div className="text-xs text-muted-foreground/70">
              {new Date(trade.executed_at || trade.created_at).toLocaleString()}
            </div>
          </div>
        ))
      ) : (
        <div className="text-center py-8 text-muted-foreground">No trades yet</div>
      )}
    </div>
  );
}

/* ---------- Tab bar used in both layouts ---------- */
function TabBar({ activeTab, setActiveTab, isMobile = false }) {
  return (
    <div className="flex border-b border-border flex-shrink-0">
      <button
        onClick={() => setActiveTab('orders')}
        className={`px-4 py-2 font-medium ${
          activeTab === 'orders' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-muted-foreground'
        }`}
      >
        Order Book
      </button>
      <button
        onClick={() => setActiveTab('trades')}
        className={`px-4 py-2 font-medium ${
          activeTab === 'trades' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-muted-foreground'
        }`}
      >
        Trade History
      </button>
      <button
        onClick={() => setActiveTab('depth')}
        className={`px-4 py-2 font-medium ${
          activeTab === 'depth' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-muted-foreground'
        }`}
      >
        Depth
      </button>
    </div>
  );
}

/* ================================================================ */
/*  TradingFloor — main page component                               */
/* ================================================================ */
function TradingFloor() {
  const { strategyId: urlStrategyId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedStrategyId, setSelectedStrategyId] = useState(urlStrategyId || '');
  const [activeTab, setActiveTab] = useState('orders');
  const { containerRef, isMobile } = useContainerMobile();
  const { connected: wsConnected, subscribe: wsSubscribe, unsubscribe: wsUnsubscribe, send: wsSend } = useWebSocket();
  const [livePrices, setLivePrices] = useState({});

  const handleStrategyUpdate = () => {
    queryClient.invalidateQueries({ queryKey: ['strategy', selectedStrategyId] });
    queryClient.invalidateQueries({ queryKey: ['strategies'] });
  };

  const { data: strategiesData, isLoading: strategiesLoading } = useQuery({
    queryKey: ['strategies'],
    queryFn: () => strategiesAPI.getAll(),
    refetchInterval: 5000,
  });

  const { data: strategyData } = useQuery({
    queryKey: ['strategy', selectedStrategyId],
    queryFn: () => strategiesAPI.getById(selectedStrategyId),
    enabled: !!selectedStrategyId,
    refetchInterval: 1000,
  });

  const { data: positionsData } = useQuery({
    queryKey: ['positions', selectedStrategyId],
    queryFn: () => positionsAPI.getByStrategy(selectedStrategyId),
    enabled: !!selectedStrategyId,
    refetchInterval: 1000,
  });

  const { data: tradesData } = useQuery({
    queryKey: ['trades', selectedStrategyId],
    queryFn: () => tradesAPI.getByStrategy(selectedStrategyId, 100),
    enabled: !!selectedStrategyId,
    refetchInterval: 1000,
  });

  const { data: plannedOrdersData } = useQuery({
    queryKey: ['plannedOrders', selectedStrategyId],
    queryFn: () => plannedOrdersAPI.getByStrategy(selectedStrategyId),
    enabled: !!selectedStrategyId,
    refetchInterval: 5000,
  });

  useEffect(() => {
    if (!selectedStrategyId && strategiesData?.data?.length > 0) {
      const runningStrategy = strategiesData.data.find((s) => s.status === 'running');
      setSelectedStrategyId((runningStrategy || strategiesData.data[0]).id);
    }
  }, [strategiesData, selectedStrategyId]);

  useEffect(() => {
    if (selectedStrategyId && selectedStrategyId !== urlStrategyId) {
      navigate(`/strategies/trading-floor/${selectedStrategyId}`, { replace: true });
    }
  }, [selectedStrategyId, urlStrategyId, navigate]);

  // Subscribe to PRICE_UPDATE WebSocket events
  useEffect(() => {
    const handlePriceUpdate = (data) => {
      if (data?.symbol && data?.price != null) {
        const norm = normalizeSymbol(data.symbol);
        setLivePrices((prev) => ({ ...prev, [norm]: data.price }));
      }
    };
    wsSubscribe('PRICE_UPDATE', handlePriceUpdate);
    return () => wsUnsubscribe('PRICE_UPDATE', handlePriceUpdate);
  }, [wsSubscribe, wsUnsubscribe]);

  const strategies = strategiesData?.data || [];
  const rawStrategy = strategyData?.data;
  const strategy = rawStrategy?.id === selectedStrategyId ? rawStrategy : null;

  const positions = strategy ? (positionsData?.data || []) : [];
  const closedToday = strategy ? (positionsData?.closedToday || []) : [];
  const trades = strategy ? (tradesData?.data || []) : [];
  const plannedOrders = strategy ? (plannedOrdersData?.data || []) : [];

  // Subscribe to the active strategy's symbol on the WebSocket
  const strategySymbol = strategy?.parameters?.symbol;
  useEffect(() => {
    if (!wsConnected || !strategySymbol) return;
    wsSend('SUBSCRIBE', { symbols: [strategySymbol] });
    return () => wsSend('UNSUBSCRIBE', { symbols: [strategySymbol] });
  }, [wsConnected, strategySymbol, wsSend]);

  const normalizedSymbol = normalizeSymbol(strategySymbol || '');
  const wsPrice = normalizedSymbol ? (livePrices[normalizedSymbol] ?? null) : null;

  // Subscribe to BAR_UPDATE for live chart streaming
  const [latestBar, setLatestBar] = useState(null);
  useEffect(() => {
    const handleBar = (data) => {
      if (!data || !strategySymbol) return;
      if (normalizeSymbol(data.symbol || '') !== normalizedSymbol) return;
      setLatestBar({
        date: new Date(data.timestamp),
        open: parseFloat(data.open),
        high: parseFloat(data.high),
        low: parseFloat(data.low),
        close: parseFloat(data.close),
        volume: parseFloat(data.volume || 0),
      });
    };
    wsSubscribe('BAR_UPDATE', handleBar);
    return () => wsUnsubscribe('BAR_UPDATE', handleBar);
  }, [wsSubscribe, wsUnsubscribe, strategySymbol, normalizedSymbol]);

  // REST price: always fetch on mount/symbol change for immediate display,
  // then poll every 60s only when WS is disconnected.
  const [restPrice, setRestPrice] = useState(null);
  useEffect(() => {
    if (!strategySymbol) return;
    let cancelled = false;
    const fetchPrice = async () => {
      try {
        const { assetsAPI } = await import('../services/api');
        const res = await assetsAPI.bars(strategySymbol, '5m', 1);
        const bars = res.data || [];
        if (!cancelled && bars.length > 0) {
          setRestPrice(parseFloat(bars[bars.length - 1].close));
        }
      } catch { /* ignore */ }
    };
    fetchPrice(); // always fetch once immediately
    const timer = wsConnected ? null : setInterval(fetchPrice, 60000);
    return () => { cancelled = true; if (timer) clearInterval(timer); };
  }, [strategySymbol, wsConnected]);

  // Seed price from orderbook mid when WS quotes haven't arrived yet
  const [obMidPrice, setObMidPrice] = useState(null);
  useEffect(() => {
    const handleOB = (data) => {
      if (!data || normalizeSymbol(data.symbol || '') !== normalizedSymbol) return;
      const bids = data.bids || [];
      const asks = data.asks || [];
      const bestBid = bids.reduce((max, l) => l.size > 0 && l.price > max ? l.price : max, 0);
      const bestAsk = asks.reduce((min, l) => l.size > 0 && (min === 0 || l.price < min) ? l.price : min, 0);
      if (bestBid > 0 && bestAsk > 0) {
        setObMidPrice(Math.round((bestBid + bestAsk) / 2 * 100) / 100);
      }
    };
    wsSubscribe('ORDERBOOK_UPDATE', handleOB);
    return () => wsUnsubscribe('ORDERBOOK_UPDATE', handleOB);
  }, [wsSubscribe, wsUnsubscribe, normalizedSymbol]);

  const livePrice = wsPrice ?? obMidPrice ?? restPrice;

  if (strategiesLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner className="h-12 w-12" />
      </div>
    );
  }

  if (strategies.length === 0) {
    return (
      <PageTransition className="p-4 sm:p-8">
        <Card title="No Strategies Found">
          <div className="text-center py-8">
            <p className="text-muted-foreground mb-4">
              No strategies found. Create a strategy first to use the Trading Floor.
            </p>
            <Button onClick={() => navigate('/strategies/builder')}>
              Create Strategy
            </Button>
          </div>
        </Card>
      </PageTransition>
    );
  }

  return (
    <PageTransition className="h-full flex flex-col p-3 sm:p-4 lg:p-6 gap-3 sm:gap-5">
      <div ref={containerRef} className="flex flex-col flex-1 min-h-0 gap-3 sm:gap-5">
        {/* Loading State — covers strategy switch transitions */}
        {!strategy && selectedStrategyId && (
          <>
            <StrategyDetails
              strategy={null}
              positions={[]}
              strategies={strategies}
              selectedStrategyId={selectedStrategyId}
              onStrategyChange={setSelectedStrategyId}
              onStrategyUpdate={handleStrategyUpdate}
            />
            <div className="flex justify-center py-12">
              <Spinner className="h-12 w-12" />
            </div>
          </>
        )}

        {/* ======================== MOBILE LAYOUT ======================== */}
        {strategy && isMobile && (
          <div className="flex flex-col gap-3 overflow-y-auto">
            {/* Mobile strategy header */}
            <MobileStrategyHeader
              strategy={strategy}
              positions={positions}
              closedToday={closedToday}
              strategies={strategies}
              selectedStrategyId={selectedStrategyId}
              onStrategyChange={setSelectedStrategyId}
              onStrategyUpdate={handleStrategyUpdate}
              livePrice={livePrice}
            />

            {/* Chart — fills most of the initial viewport */}
            <div className="min-h-[50vh] rounded-xl border border-white/8 bg-card/40 backdrop-blur-md text-card-foreground shadow-sm p-3 flex flex-col">
              <TradingChart
                strategy={strategy}
                trades={trades}
                positions={positions}
                isMobile={true}
                refreshInterval={wsConnected ? 300000 : 30000}
                streamData={latestBar}
              />
            </div>

            {/* Positions */}
            <div className="rounded-xl border border-white/8 bg-card/40 backdrop-blur-md text-card-foreground shadow-sm p-3 flex flex-col">
              <div className="overflow-y-auto">
                {positions.length > 0 || closedToday.length > 0 ? (
                  <PositionPanel positions={positions} closedToday={closedToday} />
                ) : (
                  <div className="text-center py-4 text-muted-foreground text-sm">No open positions</div>
                )}
              </div>
            </div>

            {/* Order Book / Trade History — scroll down to see, fills most of viewport */}
            <div className="min-h-[40vh] rounded-xl border border-white/8 bg-card/40 backdrop-blur-md text-card-foreground shadow-sm flex flex-col">
              <TabBar activeTab={activeTab} setActiveTab={setActiveTab} isMobile />
              <div className="flex-1 min-h-0 flex flex-col p-4">
                {activeTab === 'orders' && (
                  <OrderBook
                    strategyId={selectedStrategyId}
                    strategy={strategy}
                    plannedOrders={plannedOrders}
                    isMobile={true}
                  />
                )}
                {activeTab === 'trades' && <TradeHistoryList trades={trades} />}
                {activeTab === 'depth' && <MarketDepth symbol={strategySymbol} />}
              </div>
            </div>
          </div>
        )}

        {/* ======================== DESKTOP LAYOUT ======================== */}
        {strategy && !isMobile && (
          <div className="grid grid-cols-1 lg:grid-cols-8 lg:grid-rows-[auto_1fr_auto] gap-5 flex-1 min-h-0">
            {/* Right sidebar — 2 cols, spans all rows */}
            <div className="order-last lg:order-none max-h-[500px] lg:max-h-none lg:col-span-2 lg:row-span-3 lg:row-start-1 lg:col-start-7 rounded-xl border border-white/8 bg-card/40 backdrop-blur-md text-card-foreground shadow-sm flex flex-col min-h-0">
              <TabBar activeTab={activeTab} setActiveTab={setActiveTab} />
              <div className="flex-1 min-h-0 flex flex-col p-4">
                {activeTab === 'orders' && (
                  <OrderBook
                    strategyId={selectedStrategyId}
                    strategy={strategy}
                    plannedOrders={plannedOrders}
                  />
                )}
                {activeTab === 'trades' && <TradeHistoryList trades={trades} />}
                {activeTab === 'depth' && <MarketDepth symbol={strategySymbol} />}
              </div>
            </div>

            {/* Strategy Header — 6 cols, auto-height row */}
            <div className="order-first lg:order-none lg:col-span-6 lg:col-start-1">
              <StrategyDetails
                strategy={strategy}
                positions={positions}
                closedToday={closedToday}
                strategies={strategies}
                selectedStrategyId={selectedStrategyId}
                onStrategyChange={setSelectedStrategyId}
                onStrategyUpdate={handleStrategyUpdate}
                livePrice={livePrice}
              />
            </div>

            {/* Chart — 6 cols, fills remaining height */}
            <div className="order-2 lg:order-none min-h-[300px] sm:min-h-[400px] lg:min-h-0 lg:col-span-6 lg:col-start-1 rounded-xl border border-white/8 bg-card/40 backdrop-blur-md text-card-foreground shadow-sm p-3 flex flex-col">
              <TradingChart
                strategy={strategy}
                trades={trades}
                positions={positions}
                refreshInterval={wsConnected ? 300000 : 30000}
                streamData={latestBar}
              />
            </div>

            {/* Active Positions — 6 cols, fixed height */}
            <div className="order-3 lg:order-none lg:col-span-6 lg:col-start-1 h-auto max-h-64 sm:h-36 rounded-xl border border-white/8 bg-card/40 backdrop-blur-md text-card-foreground shadow-sm p-3 flex flex-col">
              <div className="flex-1 overflow-y-auto">
                {positions.length > 0 || closedToday.length > 0 ? (
                  <PositionPanel positions={positions} closedToday={closedToday} />
                ) : (
                  <div className="text-center py-4 text-muted-foreground text-sm">No open positions</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </PageTransition>
  );
}

export default TradingFloor;
